/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import {
  Badge,
  InformationGrid,
  InformationRow,
  StatRow,
} from "~/components/design-system/components"
import {
  InspectorCard,
  InspectorNavControls,
  useCloseInspector,
  useInspectorExpandKey,
} from "~/components/design-system/pages"
import {
  CategoryBadge,
  FlagLink,
  FlagSelect,
  LinearLinkView,
  ReviewDate,
} from "~/features/feature-flags/client/FlagControls"
import {
  useFlagPanel,
  useRequestFlagChange,
} from "~/features/feature-flags/client/FlagPanelProvider"
import { FlagStatusBadge } from "~/features/feature-flags/client/FlagStatusBadge"
import { toUTCDateString } from "~/utils/datetime"
import {
  canTurnDefaultOn,
  CUSTOM_DEFAULT_REASON,
} from "~/features/feature-flags/shared/changeRules"
import { overrideSummary, type NamedOverride } from "~/features/feature-flags/client/model/flagPage"
import { countTargets, flagReview } from "~/features/feature-flags/client/model/labels"
import {
  FEATURE_FLAGS,
  type FeatureFlagKey,
  type FlagDefinition,
} from "~/features/feature-flags/shared/registry"
import { flagStatus } from "~/features/feature-flags/client/model/status"

/** The status, with the default: the inspector's one quick action. */
function FlagStatusCard({
  flagKey,
  definition,
}: {
  flagKey: FeatureFlagKey
  definition: FlagDefinition
}) {
  const panel = useFlagPanel()
  const requestChange = useRequestFlagChange()
  const persistKey = useInspectorExpandKey("feature-flag-status")
  const current = panel.snapshot.defaultValue(flagKey)
  return (
    <InspectorCard
      title="Status"
      persistKey={persistKey}
      defaultExpanded
      contentClassName="flex flex-col gap-3"
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2">
        <span className="label-sm text-fg-muted">Status</span>
        <span className="label-sm text-fg-muted">Default</span>
        <FlagStatusBadge status={flagStatus(panel, flagKey)} className="min-w-0" />
        <FlagSelect
          ariaLabel="Default"
          value={current}
          // A custom flag's default can be on from before this rule: it can still be turned off.
          disabledReason={
            panel.lockReason(flagKey, "default") ??
            (!current && !canTurnDefaultOn(flagKey) ? CUSTOM_DEFAULT_REASON : null)
          }
          onChange={(value) => value !== null && requestChange({ kind: "default", flagKey, value })}
        />
      </div>
      {definition.category === "custom" && (
        <p className="paragraph-sm text-fg-subtle max-w-[50ch]">{CUSTOM_DEFAULT_REASON}</p>
      )}
    </InspectorCard>
  )
}

/** A description that only repeats the name says nothing, so it isn't shown. */
const hasOwnDescription = (definition: FlagDefinition) =>
  definition.description.trim().toLowerCase() !== definition.name.trim().toLowerCase()

/** What only a custom flag has: the companies it was built for, why, and its review date. */
function CustomRows({
  flagKey,
  definition,
}: {
  flagKey: FeatureFlagKey
  definition: FlagDefinition
}) {
  const panel = useFlagPanel()
  const review = flagReview(definition, toUTCDateString(new Date()))
  if (definition.category !== "custom" || !review) return null
  return (
    <>
      <InformationRow
        title="Built for"
        list={panel.builtFor(flagKey).map(panel.companyName)}
        noTruncate
      />
      <InformationRow title="Reason" value={definition.reason} noTruncate />
      <InformationRow title="Review by" valueComponent={<ReviewDate review={review} />} />
    </>
  )
}

function Prerequisites({ definition }: { definition: FlagDefinition<FeatureFlagKey> }) {
  const panel = useFlagPanel()
  if (!definition.requires?.length) return <span className="label-base">None</span>
  return (
    <ul className="flex flex-col gap-1">
      {definition.requires.map((key) => (
        <li key={key} className="flex items-center gap-2">
          <FlagLink flagKey={key} />
          <FlagStatusBadge status={flagStatus(panel, key)} />
        </li>
      ))}
    </ul>
  )
}

/** Key, category, owner, Linear, custom flag details and prerequisites. */
function FlagDetailsCard({
  flagKey,
  definition,
}: {
  flagKey: FeatureFlagKey
  definition: FlagDefinition<FeatureFlagKey>
}) {
  const persistKey = useInspectorExpandKey("feature-flag-details")
  return (
    <InspectorCard title="Details" persistKey={persistKey} contentClassName="flex flex-col gap-3">
      {hasOwnDescription(definition) && (
        <p className="paragraph-sm text-fg-base max-w-[50ch]">{definition.description}</p>
      )}
      <InformationGrid>
        <InformationRow
          title="Key"
          valueComponent={<code className="paragraph-sm font-mono text-fg-base">{flagKey}</code>}
        />
        <InformationRow
          title="Category"
          valueComponent={<CategoryBadge category={definition.category} />}
        />
        <InformationRow title="Owner" value={definition.owner} noTruncate />
        {definition.linear && (
          <InformationRow
            title="Linear"
            valueComponent={<LinearLinkView issue={definition.linear} />}
          />
        )}
        <CustomRows flagKey={flagKey} definition={definition} />
        <InformationRow
          title="Requires"
          valueComponent={<Prerequisites definition={definition} />}
        />
      </InformationGrid>
    </InspectorCard>
  )
}

const overrideCountLabel = (companies: number, users: number) => {
  const parts = [
    companies > 0 && countTargets(companies, "company"),
    users > 0 && countTargets(users, "user"),
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(" · ") : "None"
}

const OVERRIDE_LIST_LIMIT = 15

function OverrideList({ overrides }: { overrides: readonly NamedOverride[] }) {
  const hidden = overrides.length - OVERRIDE_LIST_LIMIT
  return (
    <div className="flex flex-col gap-1 paragraph-sm text-fg-base">
      {overrides.slice(0, OVERRIDE_LIST_LIMIT).map((o) => (
        <div key={`${o.scope}:${o.targetId}`} className="flex items-center justify-between gap-4">
          <span className="min-w-0 truncate">
            {o.name}
            {o.scope === "user" && <span className="text-fg-muted"> · User</span>}
          </span>
          <FlagStatusBadge status={o.value} />
        </div>
      ))}
      {hidden > 0 && <span className="text-fg-muted">+{hidden} more</span>}
    </div>
  )
}

/** Company and user override counts, with Hakio staff overrides listed. */
function FlagOverridesCard({ flagKey }: { flagKey: FeatureFlagKey }) {
  const panel = useFlagPanel()
  const persistKey = useInspectorExpandKey("feature-flag-overrides")
  const { companyCount, userCount, staff, listed } = overrideSummary(panel, flagKey)
  const total = listed.length + staff.length
  const counts = (
    <StatRow
      label="Companies and users"
      value={overrideCountLabel(companyCount, userCount)}
      weight="muted"
    />
  )

  return (
    <InspectorCard
      title="Overrides"
      persistKey={persistKey}
      defaultExpanded
      badges={
        total > 0 && (
          <Badge variant="neutral" size="xs">
            {total}
          </Badge>
        )
      }
      contentClassName="flex flex-col gap-3"
    >
      {counts}
      {listed.length > 0 && <OverrideList overrides={listed} />}
      {staff.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <h3 className="label-sm text-fg-subtle">Hakio staff</h3>
          <ul className="flex flex-col gap-1">
            {staff.map((override) => (
              <li key={override.targetId} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate paragraph-sm text-fg-base">{override.name}</span>
                <FlagStatusBadge status={override.value} description={override.note} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </InspectorCard>
  )
}

/** A flag's status and default, details and overrides, beside the flag page. */
export function FlagInspector({ flagKey }: { flagKey: FeatureFlagKey }) {
  const definition = FEATURE_FLAGS[flagKey]
  const closeInspector = useCloseInspector()
  return (
    <>
      <InspectorNavControls title={definition.name} onClear={closeInspector} />
      <FlagDetailsCard flagKey={flagKey} definition={definition} />
      <FlagStatusCard flagKey={flagKey} definition={definition} />
      <FlagOverridesCard flagKey={flagKey} />
    </>
  )
}
